import type {
  QueryClient,
  QueryKey,
  UseMutationOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import type { AsyncError } from "../lib/errors";
import { toAsyncError } from "../lib/errors";

/**
 * Shared logic for the admin billing-exempt list screens (users and orgs).
 *
 * Both screens are the same interaction: a searchable, paginated list whose
 * rows each have an optimistic `billing_exempt` toggle. They differ only in the
 * generated query/mutation functions, the field holding the array, and the id
 * used to match a row. This hook captures everything else — the optimistic
 * cache update, rollback, success merge, per-row "toggling" set, and derived
 * loading/error state — so the per-entity hooks (`useAdminUsers`,
 * `useAdminOrgs`) stay thin. The caller owns its `useQuery` (where the
 * generated types infer cleanly) and passes the result in.
 */

/** A list response shaped `{ [listKey]: Entity[]; total_count: number }`. */
type AdminListResponse<Entity, ListKey extends string> = {
  total_count: number;
} & { [K in ListKey]: Entity[] };

interface ToggleVars {
  id: string;
  billingExempt: boolean;
}

interface ToggleContext {
  previousQueries: [unknown, unknown][];
  id: string;
}

type AdminListQueryResult<Entity, ListKey extends string> = Pick<
  UseQueryResult<AdminListResponse<Entity, ListKey>>,
  "data" | "error" | "isPending" | "isFetching"
>;

interface ToggleDeps<
  Entity extends { billing_exempt: boolean },
  ListKey extends string,
> {
  queryClient: QueryClient;
  queryKey: QueryKey;
  listKey: ListKey;
  idOf: (entity: Entity) => string;
  mutationFn: (vars: ToggleVars) => Promise<Entity | undefined>;
  setTogglingIds: Dispatch<SetStateAction<Set<string>>>;
}

interface UseAdminBillingExemptListConfig<
  Entity extends { billing_exempt: boolean },
  ListKey extends string,
> {
  /** Field on the list response holding the array of entities. */
  listKey: ListKey;
  /** Stable id for an entity, used to match rows during cache updates. */
  idOf: (entity: Entity) => string;
  /** Generated query key, e.g. `listUsersApiAdminUsersGetQueryKey()`. */
  queryKey: QueryKey;
  /** Generated patch call returning the updated entity. */
  mutationFn: (vars: ToggleVars) => Promise<Entity | undefined>;
  /** Result of the caller's `useQuery` for the list. */
  query: AdminListQueryResult<Entity, ListKey>;
}

export interface AdminBillingExemptList<Entity> {
  items: Entity[];
  totalCount: number;
  isLoading: boolean;
  errorMessage: AsyncError;
  togglingIds: Set<string>;
  toggleBillingExempt: (entity: Entity) => void;
}

/** Rewrite the cached list in place by mapping its array of entities. */
function updateCachedList<
  Entity extends { billing_exempt: boolean },
  ListKey extends string,
>(
  { queryClient, queryKey, listKey }: ToggleDeps<Entity, ListKey>,
  mapItems: (items: Entity[]) => Entity[],
) {
  type Response = AdminListResponse<Entity, ListKey>;
  queryClient.setQueriesData<Response>({ queryKey }, (oldData) =>
    oldData
      ? ({ ...oldData, [listKey]: mapItems(oldData[listKey]) } as Response)
      : oldData,
  );
}

function buildToggleMutationOptions<
  Entity extends { billing_exempt: boolean },
  ListKey extends string,
>(
  deps: ToggleDeps<Entity, ListKey>,
): UseMutationOptions<Entity | undefined, Error, ToggleVars, ToggleContext> {
  const { queryClient, queryKey, idOf, mutationFn, setTogglingIds } = deps;
  return {
    mutationFn,
    onMutate: async ({ id, billingExempt }) => {
      setTogglingIds((prev) => new Set(prev).add(id));
      await queryClient.cancelQueries({ queryKey });
      const previousQueries = queryClient.getQueriesData({
        queryKey,
      }) as ToggleContext["previousQueries"];
      updateCachedList(deps, (items) =>
        items.map((entity) =>
          idOf(entity) === id
            ? { ...entity, billing_exempt: billingExempt }
            : entity,
        ),
      );
      return { previousQueries, id };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.previousQueries) {
        queryClient.setQueryData(
          key as Parameters<typeof queryClient.setQueryData>[0],
          data,
        );
      }
    },
    onSuccess: (updated) => {
      if (!updated) return;
      updateCachedList(deps, (items) =>
        items.map((entity) =>
          idOf(entity) === idOf(updated) ? updated : entity,
        ),
      );
    },
    onSettled: (_data, _error, _vars, context) => {
      if (!context) return;
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(context.id);
        return next;
      });
    },
  };
}

export function useAdminBillingExemptList<
  Entity extends { billing_exempt: boolean },
  ListKey extends string,
>(
  config: UseAdminBillingExemptListConfig<Entity, ListKey>,
): AdminBillingExemptList<Entity> {
  const { listKey, idOf, queryKey, mutationFn, query } = config;
  const queryClient = useQueryClient();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const billingToggleMutation = useMutation(
    buildToggleMutationOptions<Entity, ListKey>({
      queryClient,
      queryKey,
      listKey,
      idOf,
      mutationFn,
      setTogglingIds,
    }),
  );

  const { data, error, isPending, isFetching } = query;
  const items = data?.[listKey] ?? [];
  const totalCount = data?.total_count ?? 0;
  const errorMessage: AsyncError = error
    ? toAsyncError(error)
    : billingToggleMutation.error
      ? toAsyncError(billingToggleMutation.error)
      : null;
  const isLoading = isPending || (isFetching && !data);

  const toggleBillingExempt = (entity: Entity) => {
    billingToggleMutation.mutate({
      id: idOf(entity),
      billingExempt: !entity.billing_exempt,
    });
  };

  return {
    items,
    totalCount,
    isLoading,
    errorMessage,
    togglingIds,
    toggleBillingExempt,
  };
}
